<template>
  <v-chart class="chart" id="pieChart" :option="option" autoresize />
</template>

<script setup>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  GraphicComponent,
} from 'echarts/components';
import VChart, { THEME_KEY } from 'vue-echarts';
import { ref, provide } from 'vue';
import { colorList } from '@/config/chartColors';
import { separator } from '@/utils/format.js';


use([
  CanvasRenderer,
  PieChart,
  GridComponent,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GraphicComponent,
]);

provide(THEME_KEY, 'light');
const props = defineProps(['title'])


const nameSize = ref('narmal')
const valueSize = ref('narmalB')

// 存储原始名称的映射
const originalNames = ref({});

const option = ref({
  title: { 
    text: `{titleClass|${props.title || ''}}`,
    left: '20px',
    textStyle: { 
      color: '#192434',
      rich: {
        titleClass: {
          fontSize: 16,
          fontFamily: 'Roboto-Bold',
          padding: [0, 0, 0, 12], // Add padding to separate the border from the text
        },
      },
    }
  },
  graphic: [
    {
      type: 'rect',
      left: '20px',
      top: '3px',
      shape: {
        width: 3,
        height: 20 // 与标题字体大小一致
      },
      style: {
        fill: '#2684FC'
      }
    },
    {
      type: 'rect', // 无数据时显示
      left: 0,
      top: '24px',
      z: 10,
      shape: {
        r: 0,
        width: 1000,
        height: 1000, // 与标题字体大小一致
      },
      style: {
        fill: '#ffffff',
        stroke: '#ffffff', // 圆环颜色
        lineWidth: 2, // 圆环宽度
      }
    },
  ],
  tooltip: {
    trigger: 'item',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#2684FC',
        borderColor: '#2684FC'
      }
    },
    backgroundColor: 'rgba(15, 23, 42, 0.9)', // 设置背景颜色为黑色
    textStyle: {
      color: '#fff', // 设置文字颜色为白色
      fontSize: 14
    },
    borderColor: 'transparent', // 设置边框颜色
    borderWidth: 0, // 设置边框宽度
    padding: [10, 15], // 设置内边距
    formatter: function (params) {
      // 获取当前系列的 unit
      let separatorValue = params.value;
      if (separatorValue !== '') {
        separatorValue = separator(params.value, 3)
      } else {
        separatorValue = '-'
      }
      const unit = 'kWh'; // 如果没有定义 unit，默认使用 'kWh'
      let result = ``; // 第一行显示数据名称
      result += `
          <div style="display: flex; justify-content: space-between;">
              <span style="flex: 1;">${params.marker} ${params.name}</span>
              <span style="text-align: right;padding-left: 12px;font-weight: bold;">${separatorValue} <span style="font-weight: normal;">${unit}</span></span>
          </div>
        `;
      return result;
    }
  },
  legend: {
    left: '43%',
    top: 'center',
    width: 300,
    type: 'scroll',
    itemHeight: 8,
    itemWidth: 8,
    icon: 'circle',
    orient: 'vertical',
    pageButtonItemGap: 10, // 按钮之间的间距
    pageButtonGap: 10, // 按钮与图例之间的间距
    pageIconInactiveColor: '#ccc', // 翻页按钮禁用颜色
    pageTextStyle: {
      color: '#616D7C', // 翻页文本颜色
      fontSize: 12,
    },
    textStyle: {
      color: '#616D7C',
      rich: {
        small: {
          color: '#192434',
          width: 70,
          padding: [0, 10, 0, 5],
          fontSize: '14px',
        },
        normal: {
          color: '#192434',
          width: 90,
          padding: [0, 10, 0, 5],
          fontSize: '14px',
        },
        middle: {
          color: '#192434',
          width: 120,
          padding: [0, 10, 0, 5],
          fontSize: '14px',
        },
        large: {
          color: '#192434',
          width: 150,
          padding: [0, 10, 0, 5],
          fontSize: '14px',
        },
        smallB: {
          color: '#192434',
          width: 100,
          padding: [0, 10],
          align: 'left',
          fontSize: '13px',
          textAlign: 'right',
          backgroundColor: '#ffffff'
        },
        normalB: {
          color: '#192434',
          width: 110,
          padding: [0, 10],
          align: 'left',
          fontSize: '13px',
          textAlign: 'right',
          backgroundColor: '#ffffff'
        },
        middleB: {
          color: '#192434',
          width: 120,
          padding: [0, 10],
          align: 'left',
          fontSize: '13px',
          textAlign: 'right',
          backgroundColor: '#ffffff'
        },
        largeB: {
          color: '#192434',
          width: 150,
          padding: [0, 10],
          align: 'left',
          fontSize: '13px',
          textAlign: 'right',
          backgroundColor: '#ffffff'
        },
        c: {
          color: '#2684FC',
          padding: [6, 0, 4, 4],
          align: 'center',
          width: 48,
          fontSize: '12px',
          backgroundColor: 'rgba(38,132,252,00.1)', // 背景颜色
          borderRadius: 16, // 圆角半径
        }
      }
    },
    formatter: (name) => {
      const data = option.value.series[0].data;
      let value;
      let total = 0;
      let longestName = ''; // 取最长的name
      const length = data.length;
      for (let i = 0; i < data.length; i++) {
        total += (data[i].value-0);
        longestName = data[i].name.length > longestName.length ? data[i].name : longestName;
        if (data[i].name === name) {
          value = data[i].value;
        }
      }
      let percent = ((value / total)*100).toFixed(2);
      if (total === 0) {
        percent= ((1 / length)*100).toFixed(2);
      }
      const pieChartWidth = document.getElementById('pieChart').clientWidth;

      // 存储原始名称
      const originalName = name;
      originalNames.value[name] = originalName;

      if (pieChartWidth > 1000) {
        if (name.length > 14) {
          name = name.substr(0, 14) + '...'
        }
      } else if (pieChartWidth > 800) {
        if (name.length > 14) {
          name = name.substr(0, 14) + '...'
        }
      } else if (pieChartWidth > 600) {
        if (name.length > 14) {
          name = name.substr(0, 14) + '...'
        }
      } else {
        if (name.length > 14) {
          name = name.substr(0, 20) + '...'
        }
      }
      const separatorValue = separator(value, 3)

      return [
        `{${nameSize.value}|${name}}`,
        `{${valueSize.value}|${separatorValue} kWh}`,
        `{c|${percent}%}`
      ].join('');
    },
    // 添加 tooltip 配置
    tooltip: {
      show: true,
      formatter: function(params) {
        // 获取原始名称
        const fullName = originalNames.value[params.name] || params.name;
        return fullName;
      },
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      borderColor: 'transparent',
      borderWidth: 0,
      padding: [8, 12]
    }
  },
  grid: { // 图表边距
    left: '4%',
    right: '4%',
    top: '50',
    containLabel: true
  },
  series: [
    {
      name: '',
      type: 'pie',
      padAngle: 0,
      radius: [65, 89],
      center: ['31%', '50%'],
      emphasis: {
        itemStyle: {
          shadowBlur: 0,
        },
      },
      label: {
        show: true, // 圆圈中心
        position: 'center',
        formatter: function (item) {
          const data = option.value.series[0].data;
          const value = item.value - 0;
          let total = 0;
          for (let i = 0; i < data.length; i++) {
            total += (data[i].value - 0);
          }
          const separatorValue = separator(total, 3)
          if (value > 10**8) {
            return [
            '{b|' + separatorValue + '}\n',
            '{c|kWh}'
            ].join('');
          }
          return [
            '{a|' + separatorValue + '}\n',
            '{c|kWh}'
            ].join('');
        },
        rich: {
          a: {
            color: '#192434',
            fontSize: 18,
            fontFamily: 'D-DIN-Bold',
            padding: [5, 5],
            fontWeight: '500'
          },
          b: {
            color: '#192434',
            fontSize: 14,
            fontFamily: 'D-DIN-Bold',
            fontWeight: '500'
          },
          c: {
            color: '#616D7C',
            fontWeight: '400',
            fontSize: 14
          },
        }
      },
      toolbox: {
        show: false,
        feature: {
          mark: { show: true },
          dataView: { show: true, readOnly: false },
          restore: { show: true },
          saveAsImage: { show: true }
        }
      },
      data: [
      ],
      itemStyle: {
        color: (params) => { 
          const index = params.dataIndex;
          return colorList[index] || colorList[~~(Math.random()*20)];
        },
      }
    }
  ]
});

function getSeries(data) {
  let list = [];
  if (data) {
    const nameCount = {};
    const tempData = [];
    data.forEach((element, i) => {
      let name = element.name;
      if (nameCount[name] === undefined) {
        nameCount[name] = 0;
      } else {
        nameCount[name]++;
        name += ` (${nameCount[name]})`;
      }
      list.push({ ...element, name });
      tempData.push(element)
    })
  }
  return list;
}

function updateChart(data) {
  const series = getSeries(data);
  if (data.length) {
    const pieChartWidth = document.getElementById('pieChart').clientWidth;
    console.log('clientWidth', pieChartWidth);
    // 手动适配 500以下180；700以下200；1000以下300；1500以下400
    if (pieChartWidth > 1500) {
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['25%', '50%'],
        radius: ['50%', '70%'],
      }));
      nameSize.value = 'large';
      valueSize.value = 'largeB';
      option.value.legend.left = '43%';
    } else if (pieChartWidth > 1000) { // 1000 ~ 1500
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['25%', '50%'],
        radius: ['50%', '68%'],
      }));
      nameSize.value = 'large';
      valueSize.value = 'largeB';
      option.value.legend.left = '45%';
    } else if (pieChartWidth > 800) { // 800 ~ 1000
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['25%', '50%'],
        radius: ['50%', '68%'],
      }));
      nameSize.value = 'middle';
      valueSize.value = 'middleB';
      option.value.legend.left = '45%';
    } else if (pieChartWidth > 600) { // 600 ~ 800
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['24%', '50%'],
        radius: ['50%', '68%'],
      }));
      nameSize.value = 'normal';
      valueSize.value = 'normalB';
      option.value.legend.left = '45%';
    } else if (pieChartWidth > 500) { // 500 ~ 600
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['20%', '50%'],
        radius: ['45%', '63%'],
      }));
      nameSize.value = 'normal';
      valueSize.value = 'normalB';
      option.value.legend.left = '38%';
    } else { // < 500
      option.value.series = option.value.series.map(serie => ({
        ...serie,
        center: ['20%', '50%'],
        radius: ['40%', '58%'],
      }));
      nameSize.value = 'small';
      valueSize.value = 'smallB';
      option.value.legend.left = '35%';
    }
    option.value.series[0].data = series;
    option.value.graphic[1].z = 0;
  } else {
    // 清空
    option.value.graphic[1].z = 10;
    option.value.series[0].data = [];
  }
}

function alertWidth() {
  const pieChartWidth = document.getElementById('pieChart').clientWidth;
  window.alert(pieChartWidth)
}

defineExpose({updateChart});
</script>

<style scoped>
/* .chart {
  height: 100vh;
} */
</style>